import React, {
  useState,
  useEffect,
  createContext,
  FunctionComponent,
} from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { APP_ROOT } from 'utils/constants/urls';
import { Helmet } from 'react-helmet';

import {
  ImageType,
  CollectionMethodContentfulType,
  SubscriptionVariantType,
  FAQ,
  ProductsBySlugType,
} from 'utils/types';
import { Head } from './seo';
import Layout from '../components/Layout';
import loadable, { LoadableComponent } from '@loadable/component';

import { HeroContainer as HeroSection } from '../containers/PDP/HeroContainer';
import { generateKey } from 'utils/helpers';
import {
  WhyTrustUsContainer as WhyTrustUsSection,
  WhyTrustUsContainerProps,
} from '../containers/PDP/WhyTrustUsContainer';
import {
  DetailsContainer as DetailsSection,
  DetailsContainerProps,
} from '../containers/PDP/DetailsContainer';
import {
  // DigitalResultsContainer as DigitalResultsSection,
  DigitalResultsContainerProps,
} from '../containers/PDP/DigitalResultsContainer';
const DigitalResultsSection = loadable(() =>
  import('../containers/PDP/DigitalResultsContainer'),
);
import {
  // HowItWorksContainer as HowItWorksSection,
  HowItWorksContainerProps,
} from '../containers/PDP/HowItWorksContainer';
const HowItWorksSection = loadable(() =>
  import('../containers/PDP/HowItWorksContainer'),
);
import {
  // ReviewsContainer as ReviewsSection,
  ReviewsContainerProps,
} from '../containers/PDP/ReviewsContainer';
const ReviewsSection = loadable(() =>
  import('../containers/PDP/ReviewsContainer'),
);
import {
  FAQContainer as FAQSection,
  FAQContainerProps,
} from '../containers/PDP/FAQContainer';
import {
  // MoreTestsContainer as MoreTestsSection,
  MoreTestsContainerProps,
} from '../containers/PDP/MoreTestsContainer';
const MoreTestsSection = loadable(() =>
  import('../containers/PDP/MoreTestsContainer'),
);
import {
  // TestimonialsContainer as TestimonialsSection,
  TestimonialsContainerProps,
} from '../containers/PDP/TestimonialsContainer';
import { RatingsBarProps } from '@everlywell/leaves';
const TestimonialsSection = loadable(() =>
  import('../containers/PDP/TestimonialsContainer'),
);

import { ComparisonSection } from 'components/PDP/ComparisonSection';

const components: {
  DetailsSection: React.FunctionComponent<DetailsContainerProps>;
  WhyTrustUsSection: React.FunctionComponent<WhyTrustUsContainerProps>;
  DigitalResultsSection: LoadableComponent<DigitalResultsContainerProps>;
  HowItWorksSection: LoadableComponent<HowItWorksContainerProps>;
  ReviewsSection: LoadableComponent<ReviewsContainerProps>;
  FAQSection: React.FunctionComponent<FAQContainerProps>;
  TestimonialsSection: LoadableComponent<TestimonialsContainerProps>;
  MoreTestsSection: LoadableComponent<MoreTestsContainerProps>;
  ComparisonSection: FunctionComponent<any>;
} = {
  DetailsSection,
  WhyTrustUsSection,
  DigitalResultsSection,
  HowItWorksSection,
  ReviewsSection,
  FAQSection,
  TestimonialsSection,
  MoreTestsSection,
  ComparisonSection,
};

interface PDPPageSectionType {
  sectionId: string;
  title: string;
  componentName: string;
  slug: string;
  listOfContentSections?: {
    sectionId: string;
    title: string;
    headline?: string;
    subHeadline?: string;
    image?: ImageType;
    description?: {
      description: string;
    };
    desktopImages?: Array<ImageType>;
    mobileImages?: Array<ImageType>;
    modalButtonText?: string;
    modalTitle?: string;
    slug?: string;
    embeddedVideoLink?: string;
    listOfContentSections?: Array<{
      title: string;
      image: ImageType;
      description: {
        description: string;
      };
    }>;
  };
  modalButtonText?: string;
  description?: {
    description: string;
  };
  desktopImages?: Array<ImageType>;
  mobileImages?: Array<ImageType>;
}

interface AllProductInfoType {
  name: string;
  price: string;
  productId: number;
  pageSections: PDPPageSectionType[];
  heroSection: {
    consultAvailable: boolean;
    consultAvailableText: string;
    heroDescriptionHeader: string;
    heroDescriptionText: string;
    heroMeasuresText: string;
    heroPurchaseDetailsCallout: string;
    heroSliderImages: Array<ImageType>;
    heroTopCallout: string;
    heroBottomCallout: string;
    heroDescriptionCallout?: string;
    heroModalTitle: string;
    heroModalButtonText: string;
    heroMeasuresContentSection: {
      listOfContentSections: Array<{
        title: string;
        description: {
          description: string;
        };
        image: ImageType;
      }>;
    };
  };
  sku: string;
  slug: string;
  description: string;
  onSale: boolean;
  reviewData: {
    average_rating: number;
    review_count: number;
    rating_histogram: number[];
    recommended_ratio: number;
  };
  primaryCallouts: Array<{ text: string }>;
  markerList: {
    markers: Array<{
      title: string;
      description: {
        description: string;
      };
    }>;
  };
  metaDescription: string;
  metaKeywords: string;
  metaTitle: string;
  variantId: number;
  collectionMethods: Array<CollectionMethodContentfulType>;
  subscriptionVariants: Array<SubscriptionVariantType>;
  digDeeperSections: Array<{ question: string; answer: { answer: string } }>;
  heroImages: Array<ImageType>;
  productsBySlug: ProductsBySlugType;
  hideSubscription: any;
}

interface Props {
  pageContext: {
    productData: AllProductInfoType;
    productsBySlug: ProductsBySlugType;
    allProductPromoData: ProductPromoData[];
  };
}

interface ProductPromoData {
  slug: string;
  onSale: boolean;
  promoText: string;
}

// @ts-ignore
export const ProductInfoContext = createContext<AllProductInfoType>({});
export const AllProductPromosContext = createContext<ProductPromoData[]>([]);

const PDPTemplate = (props: Props) => {
  const { pageContext } = props;
  const { productData, productsBySlug, allProductPromoData } = pageContext;
  const { pageSections: sections, heroSection, hideSubscription } = productData;
  const {
    consultAvailable,
    consultAvailableText,
    heroMeasuresText,
    heroDescriptionHeader,
    heroDescriptionText,
    heroSliderImages,
    heroMeasuresContentSection,
    heroModalTitle,
    heroModalButtonText,
    heroDescriptionCallout,
    heroPurchaseDetailsCallout,
  } = heroSection;

  // necessary for SSR https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [hasMounted, setHasMounted] = useState<boolean>(false);

  useEffect(() => {
    setHasMounted(true);
    if (
      typeof window !== 'undefined' &&
      //hacky temporary solution until we fix Iterable events locally
      window.location.host !== 'localhost:8080'
    ) {
      window.dataLayer.push({
        event: 'pdpPageLoad',
        product: {
          id: productData.productId,
          name: productData.name,
          price: productData.price,
        },
      });
    }
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        Page: 'PDP',
      },
    });
  }, []);

  const preconnectLinks = [
    'https://dx.steelhousemedia.com',
    'https://www.google-analytics.com',
    'https://d18p8z0ptb8qab.cloudfront.net',
    'https://aa.agkn.com',
    'https://px.steelhousemedia.com',
    'https://ww.steelhousemedia.com',
    'https://cnv.event.prod.bidr.io',
    'https://everlywell.extole.io',
    'https://www.googletagmanager.com',
  ];

  return (
    <div data-testid="pdp-page">
      <Head
        title={productData.metaTitle}
        description={productData.metaDescription}
        keywords={productData.metaKeywords}
        url={`${APP_ROOT}/products/${productData.slug}/`}
      />
      <Helmet>
        {preconnectLinks.map((link: string) => (
          <link
            key={generateKey()}
            href={link}
            rel="preconnect"
            crossOrigin="anonymous"
          />
        ))}
      </Helmet>
      {sections ? (
        <Layout>
          <ProductInfoContext.Provider
            value={{
              ...productData,
              heroImages: heroSection.heroSliderImages,
              productsBySlug,
            }}
          >
            <AllProductPromosContext.Provider value={allProductPromoData}>
              <div id="banner"></div>
              <HeroSection
                consultAvailable={consultAvailable}
                consultAvailableText={consultAvailableText}
                descriptionHeader={heroDescriptionHeader}
                descriptionText={heroDescriptionText}
                descriptionCallout={heroDescriptionCallout}
                measuresText={heroMeasuresText}
                modalButtonText={heroModalButtonText}
                modalTitle={heroModalTitle}
                listOfMeasuredItems={heroMeasuresContentSection}
                sliderImages={heroSliderImages}
                hideSubscription={hideSubscription}
                ctaSubheadline={
                  heroPurchaseDetailsCallout ||
                  'Free Shipping • FSA / HSA accepted' // This is the default value unless specified in Contentful
                }
              />
              {sections.map(section => {
                // @ts-ignore
                if (components[section.componentName]) {
                  return React.createElement(
                    // @ts-ignore
                    components[section.componentName],
                    {
                      content: section,
                      key: section.componentName,
                    },
                  );
                }
              })}
            </AllProductPromosContext.Provider>
          </ProductInfoContext.Provider>
        </Layout>
      ) : null}
    </div>
  );
};

export default PDPTemplate;
